// Shared helpers for the public number lists (Info + Order on every country row) and the Info dialog.
// Everything shown comes from /api/public/coverage (number offers, country regulations, outgoing-call flag);
// nothing is invented per country, and a field that is not in the data is left out.
import { portalUrl } from '../config/site';

// Public number groups in display order, with the number kinds each covers (UC numbers are 2-way numbers).
export const PUBLIC_GROUPS = [
  { key: 'local', label: 'Local', kinds: ['local'], flag: 'local' },
  { key: 'national', label: 'National', kinds: ['national'], flag: 'national' },
  { key: 'mobile', label: 'Mobile', kinds: ['mobile'], flag: 'mobile' },
  { key: '2way', label: '2-Way', kinds: ['2way', 'uc'], flag: 'twoway' },
  { key: 'tollfree', label: 'Toll-Free', kinds: ['tollfree'], flag: 'itfs' },
  { key: 'uifn', label: 'UIFN', kinds: ['uifn'], flag: 'uifn' },
];
export const publicGroup = (key) => PUBLIC_GROUPS.find((g) => g.key === key);

/** Group keys a country record (utils/coverage toCountryRecord) offers, in display order. */
export const groupsOf = (record) => PUBLIC_GROUPS.filter((g) => record && record[g.flag]).map((g) => g.key);

// Short address rule under the country name in the lists (backend services/coverage.ADDRESS_SHORT).
export const ADDRESS_SHORT = {
  country: 'Address: in country',
  region: 'Address: local area',
  anywhere: 'Address: anywhere',
};

// Documents summary (backend services/coverage.summarise_rules).
export const DOCS_BADGE = {
  required: { label: 'Documents required', tone: 'amber' },
  none: { label: 'No documents required', tone: 'green' },
  on_order: { label: 'Documents confirmed on order', tone: 'gray' },
};

const PORTAL_FALLBACK = 'https://portal.voip2.co.uk';

/**
 * Where Order goes: the customer portal's Get Numbers page with the country (and number type) preselected.
 * Logged-out visitors sign in first and come back to the same page.
 */
export const orderUrl = (country, group) => {
  const q = new URLSearchParams({ country });
  if (group) q.set('type', group);
  const path = `/customer/get-numbers?${q.toString()}`;
  const url = portalUrl(path);
  // The website build has no portal routes: without REACT_APP_PORTAL_URL, link to the live portal.
  if (process.env.REACT_APP_TARGET === 'site' && !/^https?:\/\//.test(url)) return `${PORTAL_FALLBACK}${path}`;
  return url;
};

const sentence = (v) => (v ? `${v[0].toUpperCase()}${v.slice(1)}` : v);

/** A long value cut at a word boundary, e.g. a carrier's Caller ID note. */
const clip = (v, max = 64) => {
  if (!v || v.length <= max) return v;
  const cut = v.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(' ') > 40 ? cut.lastIndexOf(' ') : max).replace(/[,.;:\s]+$/, '')}…`;
};

/**
 * Everything the Info dialog shows for one number group of a country detail (/api/public/coverage/{country}):
 * { offered, documents, toFollow, address, rules, facts, leadTime, outbound, hasRule }.
 */
export const groupDetails = (data, groupKey) => {
  const g = publicGroup(groupKey);
  if (!g || !data) return null;
  const offers = (data.offers || []).filter((o) => g.kinds.includes(o.number_kind));
  const rules = (data.rules || []).filter((r) => (r.kinds || []).some((k) => g.kinds.includes(k)));
  const uniq = (xs) => [...new Set(xs.filter(Boolean))];
  const facts = [];
  rules.forEach((r) => (r.facts || []).forEach((f) => {
    if (f.value && !facts.some((x) => x.label === f.label)) facts.push({ label: f.label, value: clip(sentence(f.value)) });
  }));
  const leads = uniq(offers.map((o) => o.lead_time));
  return {
    offered: offers.length > 0,
    variants: uniq(offers.map((o) => o.label)),
    documents: uniq(rules.flatMap((r) => r.documents || [])),
    toFollow: rules.some((r) => r.to_follow),
    address: uniq(rules.map((r) => r.address)),
    rules: uniq(rules.flatMap((r) => r.rules || [])),
    facts,
    leadTime: leads.length ? leads.join(' / ') : null,
    outbound: g.key === '2way' ? Boolean(data.outbound_available) : null,
    hasRule: rules.length > 0,
  };
};

/** Documents badge for one group's details: required | none | on_order | null (no regulations listed). */
export const docsState = (d) => {
  if (!d || !d.hasRule) return null;
  if (d.documents.length) return 'required';
  return d.toFollow ? 'on_order' : 'none';
};
